import { IGetModifiedGlobalCreators } from "@shared/lib/types";

import { ToastService } from "@shared/lib/services";
import { IFilTerValue } from "@shared/lib/types/globalDiscovery";
import { isNumber } from "@shared/lib/utils/isValueExists";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { SORT_OPTIONS } from "../constants";
import { GlobalDiscoveryService } from "../services/globalDiscovery.service";
import { cleanseFilters } from "../utils/cleanseFilters";

interface SelectedFieldState {
  data: IGetModifiedGlobalCreators | undefined;
  selectedFilters: IFilTerValue;
  backendFilters: IFilTerValue;
  platformId: string;
  isLoadingData: boolean;
  totalCount?: number;
  currentPage: number;
  pageSize: number;
}

interface SelectedFieldActions {
  fetchData: () => Promise<void>;
  setPageSize: (pageSize: number) => void;
  setCurrentPage: (page: number) => void;
  setPlatform: (platformId: string) => void;
  resetSelectedFilters: () => void;
  saveFilters: (toFetch?: boolean) => void;
  setSelectedFilter: (value: (value: IFilTerValue) => IFilTerValue) => void;
  setFiltersFromUrl: (
    filters: { [key: string]: IFilTerValue },
    platformId: string,
    pageNo?: number
  ) => void;
  reset: () => void;
}

const getInitialState = (): SelectedFieldState => ({
  data: undefined,
  selectedFilters: {},
  backendFilters: {},
  platformId: "",
  // platformId: insightsiqPlatformWorkIdMap["instagram"],
  isLoadingData: false,
  currentPage: 1,
  pageSize: 20,
});

export const useGetGlobalDiscoveryStore = create<
  SelectedFieldState & SelectedFieldActions
>()(
  devtools((set, get) => ({
    ...getInitialState(),

    fetchData: (() => {
      let activeRequestId = 0;

      return async (): Promise<void> => {
        const { platformId, backendFilters, pageSize, currentPage } = get();

        if (!platformId) {
          ToastService.error("Please select a platform to fetch data");
          return;
        }

        const cleansedFilters = cleanseFilters(backendFilters);
        set({ isLoadingData: true });

        // Track the latest request
        const requestId = ++activeRequestId;

        try {
          const response = await GlobalDiscoveryService.getCreators({
            searchParams: cleansedFilters,
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
            work_platform_id: platformId,
          });

          const data: IGetModifiedGlobalCreators = await response.data;

          // Only update state if this is the latest request
          if (requestId === activeRequestId) {
            const totalCount = data.metadata?.total_results;
            set({
              data,
              totalCount,
            });
          }
        } catch (error) {
          if (requestId === activeRequestId) {
            console.error("Error fetching influencers:", error);
            ToastService.error("Error fetching influencers");
          }
        } finally {
          if (requestId === activeRequestId) {
            set({ isLoadingData: false });
          }
        }
      };
    })(),

    setPageSize: (pageSize: number) => {
      set({ pageSize, currentPage: 1 });
    },

    setCurrentPage: (page: number) => {
      set({ currentPage: page });
    },

    setPlatform: (platformId: string) => {
      set({ platformId: platformId });
    },

    resetSelectedFilters: () => {
      set((state) => ({
        selectedFilters: {
          sort_by: SORT_OPTIONS[0].value,
          filterTags: { sort_by: SORT_OPTIONS[0].value.field },
        },
      }));
    },
    saveFilters: (toFetch = true) => {
      const { selectedFilters, fetchData } = get();
      // Save selectedFilters to backendFilters (committed state)
      set(() => ({ backendFilters: selectedFilters, currentPage: 1 }));
      // Trigger fetch with new filters, reset to page 1
      if (toFetch) {
        fetchData();
      }
    },

    setSelectedFilter: (updateFn: (value: IFilTerValue) => IFilTerValue) => {
      set((state) => {
        return { selectedFilters: updateFn(state?.selectedFilters) };
      });
    },
    setFiltersFromUrl: (
      filters: { [key: string]: IFilTerValue },
      platfomId: string,
      pagNo?: number
    ) => {
      const { currentPage } = get();
      set({
        backendFilters: filters,
        selectedFilters: filters,
        platformId: platfomId,
        currentPage: isNumber(pagNo) ? pagNo : currentPage,
      });
    },

    reset: () =>
      set((state) => ({
        ...getInitialState(),
        // platformId: state.platformId,
      })),
  }))
);
